import React, {
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  usePresence,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import LoaderBlack from "@/assets/loader-black.gif";
import LoaderWhite from "@/assets/loader-white.gif";

type Size = "sm" | "md" | "lg";

interface LoaderProps {
  variant?: "default" | "black" | "white";
  size?: Size;
  message?: string;
}

const sizeClasses: Record<Size, string> = {
  sm: "w-16 h-16",
  md: "w-24 h-24",
  lg: "w-32 h-32",
};

// Radius of the gif in px (half of the sizes above: 64, 96, 128).
const GIF_RADIUS: Record<Size, number> = { sm: 32, md: 48, lg: 64 };

// The loader never starts leaving before this much time has passed since it
// appeared. Raise it for a longer hold (the enter animation needs ~1000).
const MIN_VISIBLE_MS = 2200;

// ENTER: 1) gif zooms in, 2) background closes in around it
const GIF_IN_DURATION = 0.5;
const GIF_IN_EASE = [0.16, 1, 0.3, 1] as const;
const BG_IN_DELAY = 0.3;
const BG_IN_DURATION = 0.7;
const BG_IN_EASE = [0.16, 1, 0.3, 1] as const;

// EXIT: 1) background opens out from the gif's edge, 2) gif zooms away
const BG_OUT_DURATION = 1.0;
const BG_OUT_EASE = [0.7, 0, 0.84, 0] as const;
const GIF_OUT_DURATION = 0.5;
const GIF_OUT_SCALE = 3;

/* ---------- Full-screen visuals (same animation as before) ---------- */
const FullScreenLoader: React.FC<{ size: Size; message?: string }> = ({
  size,
  message,
}) => {
  const startR = GIF_RADIUS[size];

  const endR = useMemo(
    () =>
      typeof window === "undefined"
        ? 1500
        : Math.hypot(window.innerWidth, window.innerHeight) / 2 + 4,
    [],
  );

  const reduceMotion = useReducedMotion();

  const r = useMotionValue(reduceMotion ? 0 : endR);
  const edge = useTransform(r, (v) => v + 1);
  const mask = useMotionTemplate`radial-gradient(circle at 50% 50%, transparent ${r}px, #000 ${edge}px)`;

  const gifScale = useMotionValue(reduceMotion ? 1 : 0.3);
  const gifOpacity = useMotionValue(reduceMotion ? 1 : 0);

  const running = useRef<{ stop: () => void }[]>([]);
  const mountedAt = useRef(Date.now());
  const [isPresent, safeToRemove] = usePresence();

  const stopAll = () => {
    running.current.forEach((c) => c.stop());
    running.current = [];
  };

  // ENTER
  useEffect(() => {
    if (reduceMotion) return;

    running.current = [
      animate(gifScale, 1, { duration: GIF_IN_DURATION, ease: GIF_IN_EASE }),
      animate(gifOpacity, 1, { duration: GIF_IN_DURATION * 0.6 }),
      animate(r, 0, {
        delay: BG_IN_DELAY,
        duration: BG_IN_DURATION,
        ease: BG_IN_EASE,
      }),
    ];

    return stopAll;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // EXIT (waits out the minimum time first)
  useEffect(() => {
    if (isPresent) return;

    if (reduceMotion) {
      safeToRemove?.();
      return;
    }

    const startExit = () => {
      stopAll();
      gifScale.set(1);
      gifOpacity.set(1);
      r.set(startR);

      const bgOut = animate(r, endR, {
        duration: BG_OUT_DURATION,
        ease: BG_OUT_EASE,
        onComplete: () => {
          running.current = [
            animate(gifScale, GIF_OUT_SCALE, {
              duration: GIF_OUT_DURATION,
              ease: [0.4, 0, 1, 1],
            }),
            animate(gifOpacity, 0, {
              duration: GIF_OUT_DURATION,
              ease: "easeIn",
              onComplete: () => safeToRemove?.(),
            }),
          ];
        },
      });
      running.current = [bgOut];
    };

    const remaining = Math.max(
      0,
      MIN_VISIBLE_MS - (Date.now() - mountedAt.current),
    );
    const timer = setTimeout(startExit, remaining);

    return () => {
      clearTimeout(timer);
      stopAll();
    };
  }, [isPresent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock page scrolling while the loader covers the screen.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex select-none items-center justify-center"
      style={{ pointerEvents: isPresent ? "auto" : "none" }}
      role="status"
      aria-live="polite"
    >
      <motion.div
        className="absolute inset-0 z-0 bg-primary"
        style={{ maskImage: mask, WebkitMaskImage: mask }}
      />

      <motion.img
        src={LoaderWhite}
        alt="Loading"
        className={`relative z-10 ${sizeClasses[size]} pointer-events-none select-none`}
        style={{ scale: gifScale, opacity: gifOpacity }}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
      />

      {message && (
        <motion.p
          className="absolute inset-x-0 top-1/2 z-10 text-center text-lg font-medium text-white"
          style={{ marginTop: startR + 16, opacity: gifOpacity }}
        >
          {message}
        </motion.p>
      )}

      <span className="sr-only">Loading, please wait...</span>
    </div>
  );
};

/* ---------- The overlay lives outside your pages ---------- */
type OverlayState = {
  active: boolean;
  size: Size;
  message?: string;
  session: number; // new number = brand new loader instance
};

let state: OverlayState = { active: false, size: "md", session: 0 };
let users = 0; // how many <Loader /> components currently want it shown
let root: Root | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => state;

const Overlay: React.FC = () => {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <AnimatePresence>
      {s.active && (
        <FullScreenLoader key={s.session} size={s.size} message={s.message} />
      )}
    </AnimatePresence>
  );
};

const ensureRoot = () => {
  if (root || typeof document === "undefined") return;
  const el = document.createElement("div");
  el.id = "app-loader-root";
  document.body.appendChild(el);
  root = createRoot(el);
  root.render(<Overlay />);
};

const showOverlay = (size: Size, message?: string) => {
  ensureRoot();
  users += 1;
  if (users === 1) {
    state = { active: true, size, message, session: state.session + 1 };
    emit();
  }
};

const hideOverlay = () => {
  users = Math.max(0, users - 1);
  if (users === 0) {
    state = { ...state, active: false };
    emit();
  }
};

/* ---------- Public component ---------- */
const Loader: React.FC<LoaderProps> = ({
  variant = "default",
  size = "md",
  message,
}) => {
  const isFullScreen = variant === "default";

  // Full screen: just tell the overlay to show while this component is mounted.
  // When your page stops rendering <Loader />, the overlay plays its exit
  // (after the minimum time) on its own.
  useEffect(() => {
    if (!isFullScreen) return;
    showOverlay(size, message);
    return hideOverlay;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isFullScreen) return null;

  const loaderSrc = variant === "black" ? LoaderBlack : LoaderWhite;

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <img
        src={loaderSrc}
        alt="Loading"
        className={`${sizeClasses[size]} pointer-events-none select-none`}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
      />
      {message && (
        <p className="mt-3 text-sm text-muted-foreground">{message}</p>
      )}
      <span className="sr-only">Loading, please wait...</span>
    </div>
  );
};

export default Loader;